import type { ProgrammaticLandingPageConfig } from '../../types'
import { KWP_SIZE_FACTS } from '../kwpSizeFacts'

const FACTS = KWP_SIZE_FACTS[25]

export const KWP_25_INSTALACJA_FOTOWOLTAICZNA: ProgrammaticLandingPageConfig = {
  slug: 'instalacja-fotowoltaiczna-25-kwp-cena',
  lang: 'pl',
  topicId: 'kwp-kosten-25',
  title: 'Instalacja fotowoltaiczna 25 kWp: cena i wartości orientacyjne',
  metaDescription:
    'Ile kosztuje instalacja fotowoltaiczna 25 kWp? Powierzchnia dachu, liczba paneli, ' +
    'rozbicie kosztów i bezpłatne porównanie cen dla dużych budynków mieszkalnych.',
  keywords: [
    'instalacja fotowoltaiczna 25 kWp cena',
    'panele słoneczne 25 kWp koszt',
    'ile kosztuje instalacja 25 kWp',
    '25 kWp instalacja fotowoltaiczna budynek',
  ],
  eyebrow: 'Wielkość instalacji: 25 kWp',
  h1: 'Instalacja fotowoltaiczna 25 kWp: cena i wartości orientacyjne',
  intro:
    'Instalacja 25 kWp odpowiednia jest dla dużych budynków mieszkalnych z wieloma lokatorami lub ' +
    'bardzo wysokim łącznym zużyciem - i znajduje się dokładnie na granicy, od której w 2026 roku ' +
    'zmienia się w Niemczech system taryf EEG. Znajdziesz tu wartości orientacyjne dotyczące ' +
    'powierzchni dachu i liczby paneli, rozbicie kosztów, a tuż poniżej kalkulator z już ' +
    'wypełnioną średnią ceną dla 25 kWp, obliczoną na podstawie prawdziwych ofert firm ' +
    'rzemieślniczych.',
  calculatorPrefill: { kind: 'kwp', kwp: 25 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Dla kogo odpowiednia jest instalacja 25 kWp?' },
    {
      type: 'paragraph',
      text:
        'Wartość orientacyjna: 25 kWp zwykle pasuje do większego budynku mieszkalnego z wieloma ' +
        'lokatorami lub obiektu z bardzo wysokim łącznym zużyciem, całkowite roczne zużycie często ' +
        'przekracza 16 000 kWh. Przy kilku gospodarstwach w tym samym budynku firma rzemieślnicza ' +
        'wyjaśnia dodatkowo kwestie podziału zużycia energii.',
    },
    { type: 'heading', text: 'Dach i panele: czego potrzebuje instalacja 25 kWp' },
    {
      type: 'paragraph',
      text:
        `Wartość orientacyjna: ok. ${FACTS.roofAreaM2} m² powierzchni dachu i ok. ` +
        `${FACTS.moduleCount} paneli przy typowych modułach ~440 Wp. Roczna produkcja przy ` +
        `orientacji południowej bez zacienienia wynosi ok. ${FACTS.annualYieldKwh.toLocaleString('pl-PL')} kWh. ` +
        'Przy tej wielkości firmy rzemieślnicze zwykle rozkładają panele na kilka połaci lub ' +
        'segmentów dachu - dokładne obliczenie dla Twojego dachu daje bezpłatny kalkulator doboru ' +
        'instalacji.',
    },
    { type: 'heading', text: 'Składniki kosztów instalacji fotowoltaicznej' },
    {
      type: 'list',
      items: [
        'Panele: największa pojedyncza pozycja kosztowa, zwykle około jednej trzeciej całości',
        'Falownik: przy 25 kWp zwykle kilka stringów i falowników',
        'Montaż/robocizna: mocowanie na dachu, okablowanie na kilku segmentach',
        'Rusztowanie: przy większej powierzchni dachu odpowiednio większe',
        'Instalacja elektryczna: dostosowanie rozdzielnicy, ewentualnie kilka liczników przy wielu lokatorach',
        'Zgłoszenie: rejestracja u operatora sieci i w odpowiednich rejestrach',
      ],
    },
    {
      type: 'paragraph',
      text:
        'W budynkach z wieloma lokatorami warto wcześniej wyjaśnić, czy sensowniejszy jest model ' +
        'wspólnej autokonsumpcji między najemcami, czy zasilanie wyłącznie całego budynku - wpływa ' +
        'to zarówno na projekt techniczny, jak i strukturę kosztów.',
    },
    { type: 'heading', text: 'Amortyzacja i autokonsumpcja' },
    {
      type: 'paragraph',
      text:
        'W Niemczech rok 2026 jest ostatnim rokiem gwarantowanej stałej taryfy EEG dla nowych ' +
        'instalacji poniżej 25 kWp - kto uruchomi instalację 25 kWp w 2026 roku, wchodzi już w ' +
        'zmieniony system taryf obowiązujący od tej granicy. Do 30 kWp przychody z instalacji ' +
        'fotowoltaicznych pozostają całkowicie zwolnione z podatku dochodowego, bez konieczności ' +
        'rejestracji działalności gospodarczej. Dokładne obliczenie dla Twojego dachu daje ' +
        'bezpłatny kalkulator doboru instalacji.',
    },
  ],
  faq: [
    {
      q: 'Co oznacza granica 25 kWp w taryfie EEG w 2026 roku?',
      a: 'W Niemczech rok 2026 jest ostatnim rokiem gwarantowanej stałej taryfy EEG dla nowych instalacji poniżej 25 kWp - dla instalacji od 25 kWp obowiązuje już zmieniony system taryf.',
    },
    {
      q: 'Czy przy 25 kWp muszę zarejestrować działalność gospodarczą?',
      a: 'Nie - w Niemczech przychody z instalacji fotowoltaicznych do 30 kWp są całkowicie zwolnione z podatku dochodowego, bez rejestracji działalności i bez ewidencji przychodów i rozchodów. 25 kWp mieści się w tej granicy.',
    },
    {
      q: 'Dla jakich budynków odpowiednia jest instalacja 25 kWp?',
      a: 'Typowo dla dużych budynków mieszkalnych z wieloma lokatorami lub obiektów z bardzo wysokim łącznym zużyciem. Przy wielu lokatorach firma rzemieślnicza wyjaśnia dodatkowo kwestie podziału zużycia lub ewentualnego modelu wspólnej autokonsumpcji.',
    },
  ],
  primaryCta: { label: 'Poproś teraz o bezpłatną ofertę', action: 'customer' },
  secondaryCta: {
    label: 'Koszt instalacji fotowoltaicznej: pełny przewodnik',
    action: 'link',
    href: '/koszt-instalacji-fotowoltaicznej',
  },
  relatedLinks: [
    { label: 'Instalacja fotowoltaiczna 20 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-20-kwp-cena' },
    { label: 'Instalacja fotowoltaiczna 15 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-15-kwp-cena' },
    { label: 'Instalacja elektryczna dla fotowoltaiki', href: '/instalacja-elektryczna-fotowoltaiki' },
    { label: 'Jak działa porównanie ofert', href: '/porownaj-oferty-fotowoltaiczne' },
  ],
}
